// 2026-06-25: Phase 4 + 5 — Reports page.
// 3 tabs: Daily Handover, Planned vs Actual, Epicor Sync.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', red: '#dc2626',
  amber: '#f59e0b', dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const SHOP_COLOR = {
  MACHINE_SHOP: '#2563eb', ASSEMBLY: '#16a34a', DEBURRING: '#f59e0b',
  SPECIAL_PROCESS: '#dc2626', INSPECTION: '#9333ea', PACKING: '#6b7280', OTHER: '#9ca3af',
};

const BASE = `${window._env_.SERVER_URL2}api`;

function fmtMin(min) {
  if (min == null || isNaN(min)) return '—';
  const n = Number(min);
  if (n < 60) return n.toFixed(0) + 'm';
  return Math.floor(n / 60) + 'h ' + Math.round(n % 60) + 'm';
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return '—'; }
}

function KPI({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || BR.dark, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: BR.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DailyHandover({ customerId }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/workorder/report/daily-handover`,
                                 { params: { customer_id: customerId, on_date: date } });
      setData(r.data);
    } catch (e) { setData({ error: e?.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, [customerId, date]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading…</div>;
  if (data?.error) return <div style={{ padding: 12, color: BR.red }}>⚠ {data.error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 700 }}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
               style={{ padding: '6px 10px', border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 13 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        <KPI label="Active Ops" value={data.kpis.ops_active} color={BR.amber} sub={`${data.kpis.ops_running} running, ${data.kpis.ops_paused} paused`} />
        <KPI label="Claimed Today" value={data.kpis.ops_claimed_today} color={BR.green} sub="ops completed" />
        <KPI label="WOs Completed" value={data.kpis.wos_completed_today} color={BR.green} sub="to FG today" />
        <KPI label="Pause Events" value={data.kpis.pause_events_today} color={BR.amber} sub={fmtMin(data.kpis.total_pause_minutes) + ' total'} />
        <KPI label="Shift Date" value={data.shift_date} color={BR.dark} sub={fmtTime(data.generated_at)} />
      </div>

      <Section title={`Active operations (${data.active_operations.length})`}>
        <Table cols={['Status', 'WO #', 'Op', 'Operation', 'Work Center', 'Machine', 'Operator', 'Started']}
          rows={data.active_operations.map(o => [
            <Pill key="s" colorMap={statusColors[o.status]}>{o.status}</Pill>,
            o.wo_number, o.op_seq, o.op_name,
            <Pill key="sh" colorMap={{ bg: '#fff', text: SHOP_COLOR[o.work_center_shop_type], border: SHOP_COLOR[o.work_center_shop_type] }}>{o.work_center_code}</Pill>,
            o.machine_name || '—', o.claimed_by_operator_name || '—', fmtTime(o.started_at),
          ])}
        />
      </Section>

      <Section title={`Claimed today (${data.claimed_today.length})`}>
        <Table cols={['WO #', 'Op', 'Operation', 'Work Center', 'Operator', 'Good', 'Scrap', 'Reason', 'Plan', 'Actual', 'Claimed at', 'Source']}
          rows={data.claimed_today.map(o => [
            o.wo_number, o.op_seq, o.op_name,
            <Pill key="sh" colorMap={{ bg: '#fff', text: SHOP_COLOR[o.work_center_shop_type], border: SHOP_COLOR[o.work_center_shop_type] }}>{o.work_center_code}</Pill>,
            o.claimed_by_operator_name, o.actual_qty_good, o.actual_qty_scrap,
            o.scrap_reason || '—', fmtMin(o.planned_time_min), fmtMin(o.actual_time_min),
            fmtTime(o.claimed_at), o.claim_source,
          ])}
        />
      </Section>

      <Section title={`WOs completed today (${data.completed_wos.length})`}>
        <Table cols={['WO #', 'Part', 'Qty Ord', 'Qty Good', 'Qty Scrap', 'Completed at']}
          rows={data.completed_wos.map(w => [
            w.wo_number, w.part_name, w.qty_ordered, w.qty_completed, w.qty_scrap, fmtTime(w.completed_ts),
          ])}
        />
      </Section>

      <Section title={`Pause events today (${data.pause_events.length})`}>
        <Table cols={['WO #', 'Op', 'Operator', 'Machine', 'Duration', 'Started', 'Notes']}
          rows={data.pause_events.map(p => [
            p.wo_number, p.op_seq, p.operator_name, p.machine_name || '—',
            fmtMin(p.duration_min), fmtTime(p.clock_in_ts), <span style={{ fontSize: 11 }}>{p.notes}</span>,
          ])}
        />
      </Section>
    </div>
  );
}

function PlannedVsActual({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/workorder/report/planned-vs-actual`, { params: { customer_id: customerId } });
      setData(r.data);
    } catch (e) { setData({ error: e?.response?.data?.message || e.message }); }
    finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading…</div>;
  if (data?.error) return <div style={{ padding: 12, color: BR.red }}>⚠ {data.error}</div>;

  const k = data.kpis;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        <KPI label="Ops Claimed" value={k.ops_claimed} color={BR.dark} />
        <KPI label="Total Planned" value={fmtMin(k.total_planned_min)} color={BR.blue} />
        <KPI label="Total Actual" value={fmtMin(k.total_actual_min)} color={BR.dark} />
        <KPI label="Time Variance" value={k.time_variance_pct != null ? k.time_variance_pct + '%' : '—'}
             color={k.time_variance_pct == null ? BR.muted : k.time_variance_pct > 10 ? BR.red : k.time_variance_pct < -10 ? BR.green : BR.amber}
             sub={k.time_variance_pct > 0 ? 'over plan' : 'under plan'} />
        <KPI label="Avg Yield" value={k.avg_yield_pct != null ? k.avg_yield_pct + '%' : '—'}
             color={k.avg_yield_pct == null ? BR.muted : k.avg_yield_pct >= 95 ? BR.green : k.avg_yield_pct >= 85 ? BR.amber : BR.red}
             sub={`scrap ${k.avg_scrap_pct || 0}%`} />
      </div>

      <Section title={`Claimed operations (${data.rows.length})`}>
        <Table cols={['WO #', 'Op', 'Operation', 'Work Center', 'Good', 'Scrap', 'Yield %', 'Plan', 'Actual', 'Var %', 'Operator', 'Source']}
          rows={data.rows.map(r => [
            r.wo_number, r.op_seq, r.op_name,
            <Pill key="sh" colorMap={{ bg: '#fff', text: SHOP_COLOR[r.work_center_shop_type], border: SHOP_COLOR[r.work_center_shop_type] }}>{r.work_center_code}</Pill>,
            r.actual_qty_good, r.actual_qty_scrap,
            <span key="y" style={{ color: r.yield_pct >= 95 ? BR.green : r.yield_pct >= 85 ? BR.amber : BR.red, fontWeight: 700 }}>{r.yield_pct != null ? r.yield_pct + '%' : '—'}</span>,
            fmtMin(r.planned_time_min), fmtMin(r.actual_time_min),
            <span key="v" style={{ color: r.time_variance_pct == null ? BR.muted : r.time_variance_pct > 10 ? BR.red : r.time_variance_pct < -10 ? BR.green : BR.amber, fontWeight: 700 }}>{r.time_variance_pct != null ? r.time_variance_pct + '%' : '—'}</span>,
            r.claimed_by_operator_name, r.claim_source,
          ])}
        />
      </Section>
    </div>
  );
}

function EpicorSync({ customerId }) {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${BASE}/epicor/status`, { params: { customer_id: customerId } }),
        axios.get(`${BASE}/epicor/log`, { params: { customer_id: customerId, limit: 30 } }),
      ]);
      setStatus(s.data); setLog(l.data);
    } catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`); }
  }, [customerId]);
  useEffect(() => { refresh(); }, [refresh]);

  async function triggerPull() {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${BASE}/epicor/pull-work-orders`, { customer_id: customerId });
      setMsg(`Pull result: ${r.data.status} — ${r.data.created || 0} new WOs, ${r.data.skipped || 0} skipped, ${r.data.duration_ms}ms`);
      refresh();
    } catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`); }
    finally { setBusy(false); }
  }

  if (!status) return <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading…</div>;

  const modeColor = { off: BR.muted, dry: BR.amber, live: BR.green }[status.config.mode];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <KPI label="Pulls 24h" value={status.counts_24h.pull_ok_24h} color={BR.blue} sub={status.last_pull ? fmtTime(status.last_pull.created_ts) : '—'} />
        <KPI label="Pushes 24h" value={status.counts_24h.push_ok_24h} color={BR.green} sub={status.last_push ? fmtTime(status.last_push.created_ts) : '—'} />
        <KPI label="Errors 24h" value={status.counts_24h.errors_24h} color={status.counts_24h.errors_24h > 0 ? BR.red : BR.green} />
        <KPI label="Epicor-Sourced WOs" value={status.epicor_sourced_wos} color={BR.dark} sub="pulled from ERP" />
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Configuration</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 12 }}>
          <div><strong>Base URL:</strong> {status.config.base_url || <span style={{ color: BR.muted }}>(unset)</span>}</div>
          <div><strong>Company:</strong> {status.config.company_id || <span style={{ color: BR.muted }}>(unset)</span>}</div>
          <div><strong>Plant:</strong> {status.config.plant || <span style={{ color: BR.muted }}>(unset)</span>}</div>
          <div><strong>Poll interval:</strong> {status.config.poll_interval_min} min</div>
        </div>
        <div style={{ fontSize: 11, color: BR.muted, marginTop: 6 }}>
          Set <code>EPICOR_BASE_URL</code>, <code>EPICOR_API_KEY</code>, <code>EPICOR_COMPANY_ID</code>, <code>EPICOR_PLANT</code> in .env, then <code>EPICOR_MODE=live</code> to switch from dry mocks to real Epicor REST.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={triggerPull} disabled={busy} style={{ padding: '8px 16px', background: busy ? BR.muted : BR.blue, color: '#fff',
                                                                   border: 'none', borderRadius: 5, fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Pulling…' : '⇩ Pull Work Orders from Epicor'}
          </button>
          <button onClick={refresh} style={{ padding: '8px 14px', background: '#fff', color: BR.dark,
                                              border: `1px solid ${BR.border}`, borderRadius: 5, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>↻ Refresh</button>
        </div>
        {msg && <div style={{ marginTop: 10, padding: 8, background: '#dcfce7', color: '#14532d', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>{msg}</div>}
      </div>

      <Section title={`Sync log (last ${log.length})`}>
        <Table cols={['When', 'Direction', 'Ref', 'Operator', 'Machine', 'HTTP', 'Duration', 'Error']}
          rows={log.map(r => [
            fmtTime(r.created_ts),
            <Pill key="d" colorMap={r.direction === 'pull' ? statusColors.RELEASED : statusColors.IN_PROGRESS}>{r.direction.toUpperCase()}</Pill>,
            r.entity_ref ? r.entity_ref.slice(0, 8) : '—',
            r.operator_name || '—',
            r.machine_name || '—',
            r.http_status || '—',
            r.duration_ms ? r.duration_ms + 'ms' : '—',
            r.error_message ? <span style={{ color: BR.red, fontSize: 11 }}>{r.error_message}</span> : (r.status === 'error' ? <span style={{ color: BR.red, fontSize: 11 }}>error</span> : '—'),
          ])}
        />
      </Section>
    </div>
  );
}

const statusColors = {
  DRAFT: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
  RELEASED: { bg: '#dbeafe', text: '#1e3a8a', border: '#3b82f6' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  ON_HOLD: { bg: '#fed7aa', text: '#7c2d12', border: '#ea580c' },
  COMPLETE: { bg: '#dcfce7', text: '#14532d', border: '#16a34a' },
  CANCELLED: { bg: '#fee2e2', text: '#7a0000', border: '#dc2626' },
  PENDING: { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' },
  READY: { bg: '#dbeafe', text: '#1e3a8a', border: '#3b82f6' },
  CLAIMED: { bg: '#dcfce7', text: '#14532d', border: '#16a34a' },
  DONE: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  SKIPPED: { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' },
};

function Pill({ children, colorMap }) {
  const c = colorMap || statusColors.DRAFT;
  return <span style={{ padding: '2px 8px', fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                         background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4 }}>{children}</span>;
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${BR.border}`, fontSize: 13, fontWeight: 800 }}>{title}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows }) {
  if (!rows.length) return <div style={{ padding: 16, textAlign: 'center', color: BR.muted, fontSize: 12 }}>No data.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
            {cols.map(c => (
              <th key={c} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10,
                                    fontWeight: 800, color: BR.muted, textTransform: 'uppercase' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${BR.border}` }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '7px 10px', verticalAlign: 'middle' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QualityHolds({ customerId }) {
  const [tab, setTab] = useState('open');
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releaseHold, setReleaseHold] = useState(null);
  const [disposition, setDisposition] = useState('release_as_is');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/workorder/quality-holds`,
                                 { params: { customer_id: customerId, status: tab } });
      setHolds(r.data);
    } catch (e) { setMsg('Error: ' + (e?.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  }, [customerId, tab]);
  useEffect(() => { refresh(); }, [refresh]);

  async function doRelease() {
    if (!releaseHold) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/workorder/quality-hold/${releaseHold.id}/release`, {
        customer_id: customerId,
        supervisor_id: localStorage.getItem('userId') || 'SUP-' + (localStorage.getItem('email') || 'unknown'),
        supervisor_name: localStorage.getItem('firstName') || localStorage.getItem('email') || 'Supervisor',
        disposition,
        release_notes: releaseNotes || null,
      });
      const action = r.data.action;
      const next = r.data.released_next_op;
      setMsg('Released: ' + action + (next ? ' — next op ' + next.op_seq + ' @ ' + next.work_center_code : ''));
      setReleaseHold(null); setReleaseNotes(''); setDisposition('release_as_is');
      setTimeout(() => setMsg(null), 5000);
      refresh();
    } catch (e) {
      setMsg('Error: ' + (e?.response?.data?.message || e.message));
    } finally { setBusy(false); }
  }

  const filters = [
    { key: 'open', label: 'Open' },
    { key: 'released', label: 'Released' },
    { key: 'rework', label: 'Rework' },
    { key: 'rejected_wo_cancelled', label: 'WO Cancelled' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {filters.map(f => (
          <button key={f.key} onClick={() => setTab(f.key)}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700,
                            background: tab === f.key ? BR.orange : '#fff',
                            color: tab === f.key ? '#fff' : BR.dark,
                            border: '1px solid ' + (tab === f.key ? BR.orange : BR.border),
                            borderRadius: 6, cursor: 'pointer' }}>
            {f.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: BR.muted }}>
          {holds.length} total
        </div>
      </div>

      {msg && <div style={{ padding: 10, background: '#dbeafe', color: '#1e3a8a', borderRadius: 5, fontSize: 13, marginBottom: 10, fontWeight: 700 }}>{msg}</div>}

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading...</div>
      ) : holds.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 14, background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{tab === 'open' ? '✅' : '📋'}</div>
          {tab === 'open' ? 'No open quality holds. All WOs flowing freely.' : 'No holds in this state.'}
        </div>
      ) : (
        <Section title={`${filters.find(f => f.key === tab)?.label} Quality Holds (${holds.length})`}>
          <Table cols={['When', 'WO #', 'Op', 'Work Center', 'Operator', 'Good', 'Scrap', 'Scrap %', 'Threshold', 'Reason', 'Status', 'Action']}
            rows={holds.map(h => [
              fmtTime(h.created_ts),
              h.wo_number,
              h.triggering_op_seq + ' · ' + h.triggering_op_name,
              <Pill key="s" colorMap={{ bg: '#fff', text: SHOP_COLOR[h.triggering_op_shop] || BR.muted, border: SHOP_COLOR[h.triggering_op_shop] || BR.muted }}>{h.triggering_op_shop}</Pill>,
              h.raised_by_operator_name || '—',
              h.qty_good_at_hold,
              <span key="sc" style={{ color: BR.red, fontWeight: 700 }}>{h.qty_scrap_at_hold}</span>,
              <span key="sp" style={{ color: BR.red, fontWeight: 800 }}>{Number(h.scrap_pct || 0).toFixed(1)}%</span>,
              Number(h.threshold_pct || 5).toFixed(1) + '%',
              (h.hold_reason || '').replace(/_/g, ' '),
              <Pill key="st" colorMap={h.status === 'open' ? statusColors.IN_PROGRESS : h.status === 'rework' ? statusColors.ON_HOLD : statusColors.COMPLETE}>{h.status}</Pill>,
              h.status === 'open' ? (
                <button key="b" onClick={() => setReleaseHold(h)}
                        style={{ padding: '5px 12px', background: BR.orange, color: '#fff', border: 'none',
                                  borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  Review →
                </button>
              ) : (h.release_disposition || '—'),
            ])}
          />
        </Section>
      )}

      {releaseHold && (
        <div onClick={() => setReleaseHold(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999,
                       display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 12, width: 540, padding: 22 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: BR.dark }}>
              Review Quality Hold
            </h3>
            <div style={{ marginTop: 8, padding: 12, background: '#fef2f2', border: `1px solid ${BR.red}`, borderRadius: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {releaseHold.wo_number} · {releaseHold.triggering_op_seq} {releaseHold.triggering_op_name}
              </div>
              <div style={{ fontSize: 11, color: BR.muted, marginTop: 4 }}>
                Scrap: <strong style={{ color: BR.red }}>{releaseHold.qty_scrap_at_hold}</strong> / {releaseHold.qty_good_at_hold + releaseHold.qty_scrap_at_hold}
                {' '}(<strong style={{ color: BR.red }}>{Number(releaseHold.scrap_pct).toFixed(1)}%</strong>, threshold {Number(releaseHold.threshold_pct).toFixed(1)}%)
                <br/>
                Reason: <strong>{(releaseHold.hold_reason || '').replace(/_/g, ' ')}</strong>
                <br/>
                Raised by: <strong>{releaseHold.raised_by_operator_name}</strong>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginTop: 14, marginBottom: 4 }}>Disposition</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {[
                { v: 'release_as_is', label: '✓ Release as-is', sub: 'Good parts continue; scrap recorded' },
                { v: 'rework', label: '🔧 Rework', sub: 'Send scrap to rework, continue' },
                { v: 'scrap_batch', label: '🗑 Scrap batch', sub: 'Discard scrap, continue with good' },
                { v: 'cancel_wo', label: '⛔ Cancel WO', sub: 'Stop the whole WO' },
              ].map(o => (
                <button key={o.v} onClick={() => setDisposition(o.v)}
                        style={{ padding: 10, background: disposition === o.v ? BR.orange : '#fff',
                                  color: disposition === o.v ? '#fff' : BR.dark,
                                  border: '1.5px solid ' + (disposition === o.v ? BR.orange : BR.border),
                                  borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
                  <div>{o.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.8 }}>{o.sub}</div>
                </button>
              ))}
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginTop: 14, marginBottom: 4 }}>Notes (optional)</label>
            <textarea value={releaseNotes} onChange={e => setReleaseNotes(e.target.value)} rows={2}
                      placeholder='Why this disposition? Reference MRB # if applicable...'
                      style={{ width: '100%', padding: 8, border: `1px solid ${BR.border}`, borderRadius: 5, fontSize: 12, resize: 'vertical' }} />

            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button onClick={() => setReleaseHold(null)} style={{ flex: 1, padding: 12, background: '#fff', color: BR.dark, border: `1px solid ${BR.border}`, borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doRelease} disabled={busy}
                      style={{ flex: 2, padding: 12, background: busy ? BR.muted : BR.orange, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, cursor: 'pointer' }}>
                {busy ? 'Releasing...' : 'Confirm Release'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Phase 9 2026-06-25: Losses Report ────────────────────────────────────────
function LossesReport({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const end = new Date().toISOString();
      const start = new Date(Date.now() - days * 24 * 3600_000).toISOString();
      const r = await axios.get(`${BASE}/workorder/report/losses`, { params: { customer_id: customerId, start, end } });
      setData(r.data);
    } catch (e) { console.error(e); setData(null); }
    setLoading(false);
  }, [customerId, days]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color: BR.muted, padding: 20 }}>Loading…</div>;
  if (!data) return <div style={{ color: BR.muted, padding: 20 }}>No data.</div>;

  const t = data.totals || {};
  const totalLosses = Number(t.total_losses || 0);
  const scrapCost = Number(t.total_scrap_cost || 0);
  const reworkCost = Number(t.total_rework_cost || 0);
  const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: BR.muted }}>Window:</div>
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            background: days === d ? BR.orange : '#fff', color: days === d ? '#fff' : BR.dark,
                            border: '1px solid ' + (days === d ? BR.orange : BR.border), cursor: 'pointer' }}>
            Last {d}d
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <KpiCard label="Total Losses" value={inr(totalLosses)} color={BR.red} sub={`${Number(t.scrap_qty || 0)} pcs scrapped`} />
        <KpiCard label="Scrap Cost" value={inr(scrapCost)} color={BR.red} sub={`${t.scrap_events || 0} events`} />
        <KpiCard label="Rework Cost" value={inr(reworkCost)} color={BR.amber} sub={`${t.rework_events || 0} events`} />
        <KpiCard label="Pending NCs" value={String(t.pending_events || 0)} color={BR.blue} sub={`${t.use_as_is_events || 0} use-as-is · ${t.rtv_events || 0} RTV`} />
      </div>

      <Section title="Top 10 WOs by scrap cost">
        {(data.by_wo || []).length === 0 ?
          <div style={{ padding: 14, color: BR.muted }}>No scrap events.</div> :
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={th()}>WO</th><th style={th()}>Events</th>
                <th style={th('right')}>Scrap Qty</th><th style={th('right')}>Scrap Cost</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_wo || []).slice(0, 10).map((r) => (
                <tr key={r.wo_id} style={{ borderTop: `1px solid ${BR.border}` }}>
                  <td style={td()}>{r.wo_number}</td>
                  <td style={td()}>{r.scrap_events}</td>
                  <td style={td('right')}>{r.scrap_qty}</td>
                  <td style={td('right')}><strong>{inr(r.scrap_cost)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Section>

      <Section title="Top 10 WOs by rework cost">
        {(data.rework_by_wo || []).length === 0 ?
          <div style={{ padding: 14, color: BR.muted }}>No completed rework yet.</div> :
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={th()}>WO</th><th style={th()}>Rework Count</th>
                <th style={th('right')}>Recovered</th><th style={th('right')}>Rework Scrap</th>
                <th style={th('right')}>Rework Cost</th>
              </tr>
            </thead>
            <tbody>
              {(data.rework_by_wo || []).slice(0, 10).map((r) => (
                <tr key={r.wo_id} style={{ borderTop: `1px solid ${BR.border}` }}>
                  <td style={td()}>{r.wo_number}</td>
                  <td style={td()}>{r.rework_count}</td>
                  <td style={td('right')}>{r.qty_recovered}</td>
                  <td style={td('right')}>{r.rework_scrap}</td>
                  <td style={td('right')}><strong>{inr(r.rework_cost)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Section title="By NC Type">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={th()}>Type</th><th style={th('right')}>Count</th>
                <th style={th('right')}>Qty</th><th style={th('right')}>Scrap Cost</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_nc_type || []).map((r) => (
                <tr key={r.nc_type} style={{ borderTop: `1px solid ${BR.border}` }}>
                  <td style={td()}>{r.nc_type}</td>
                  <td style={td('right')}>{r.nc_count}</td>
                  <td style={td('right')}>{r.qty}</td>
                  <td style={td('right')}>{inr(r.scrap_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section title="By Work Center">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={th()}>WC</th><th style={th('right')}>NCs</th>
                <th style={th('right')}>Rework</th><th style={th('right')}>Scrap Cost</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_work_center || []).map((r) => (
                <tr key={r.work_center_code} style={{ borderTop: `1px solid ${BR.border}` }}>
                  <td style={td()}>{r.work_center_code}</td>
                  <td style={td('right')}>{r.nc_count}</td>
                  <td style={td('right')}>{r.rework_count}</td>
                  <td style={td('right')}>{inr(r.scrap_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', padding: 14, borderRadius: 8, border: `1px solid ${BR.border}`, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 11, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, margin: '4px 0' }}>{value}</div>
      <div style={{ fontSize: 11, color: BR.muted }}>{sub}</div>
    </div>
  );
}
function th(align) { return { padding: '8px 10px', textAlign: align || 'left', fontSize: 11, color: BR.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }; }
function td(align) { return { padding: '8px 10px', textAlign: align || 'left', color: BR.dark }; }


export default function Reports() {
  const customerId = localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID;
  const [tab, setTab] = useState('handover');
  const tabs = [
    { key: 'handover', label: 'Daily Handover' },
    { key: 'variance', label: 'Planned vs Actual' },
    { key: 'epicor', label: 'Epicor Sync' },
    { key: 'holds', label: 'Quality Holds' },
    { key: 'losses', label: 'Losses' },
  ];

  return (
    <div style={{ padding: 18, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: BR.dark }}>Reports</div>
        <div style={{ fontSize: 11, color: BR.muted }}>Daily handover · variance KPIs · Epicor ERP integration log</div>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: `1px solid ${BR.border}` }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  style={{ padding: '10px 18px', fontSize: 13, fontWeight: 700,
                            background: 'none', color: tab === t.key ? BR.orange : BR.muted,
                            border: 'none', borderBottom: `3px solid ${tab === t.key ? BR.orange : 'transparent'}`,
                            cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'handover' && <DailyHandover customerId={customerId} />}
      {tab === 'variance' && <PlannedVsActual customerId={customerId} />}
      {tab === 'epicor' && <EpicorSync customerId={customerId} />}
      {tab === 'holds' && <QualityHolds customerId={customerId} />}
      {tab === 'losses' && <LossesReport customerId={customerId} />}
    </div>
  );
}
